import React, { useRef, useEffect } from "react";
import { useRouter } from "next/router";
import {
  NewModal as Modal,
  NewTypography as Typography,
  NewBox as Box,
  NewLink as Link,
  NewButton as Button,
} from "@bca/ui-library";

import type { BatteryGradeSlideoutData } from "../StoreType";
import { MechanicalReportAnalytics } from "../analytics/MechanicalReportAnalytics";
import { batterySlideoutDataPush } from "../analytics/batterySlideoutDataPush";
import BatteryGradesTable from "./BatteryGradesTable/BatteryGradesTable";

interface BatteryGradeSlideoutProps {
  isOpen: boolean;
  onClose: () => void;
  data: BatteryGradeSlideoutData;
}

const BatteryGradeSlideout = ({
  isOpen,
  onClose,
  data,
}: BatteryGradeSlideoutProps) => {
  const router = useRouter();

  const modalBody = useRef<HTMLInputElement>(null);

  useEffect(() => {
    modalBody.current?.closest(".ModalBody").scrollTo({ top: 0 });
  }, [isOpen]);

  const { catalogueRef } = router.query;
  const { categoryMapping } = data?.vehicleInformation?.assessment || {};

  const { type: reportType } =
    data?.vehicleInformation?.reportUrls?.find(
      (report) => report.rel === "mechanicalreporturl"
    ) || {};

  const mechReportCombo = MechanicalReportAnalytics({
    reportType,
    categoryMapping,
  });

  const handleViewReport = () => {
    batterySlideoutDataPush({
      mechReportCombo,
      isTracked: data?.vehicleInformation?.isTracked,
      note: "",
      catalogueRef,
      linkType: "report",
      type: "pdf",
      vehicleInfo: data.vehicleInformation,
    });

    window.open(data.reportUrl, "_blank");
  };

  const handleTermsAndConditions = () => {
    batterySlideoutDataPush({
      mechReportCombo,
      isTracked: data?.vehicleInformation?.isTracked,
      note: "",
      catalogueRef,
      linkType: "terms-conditions",
      vehicleInfo: data.vehicleInformation,
    });

    window.open(
      "http://www.bca.co.uk/legal/terms-conditions/ev-battery-grading-terms",
      "_blank"
    );
  };

  const handleBatteryHealthGrading = () => {
    batterySlideoutDataPush({
      mechReportCombo,
      isTracked: data?.vehicleInformation?.isTracked,
      note: "",
      catalogueRef,
      linkType: "more-info",
      vehicleInfo: data.vehicleInformation,
    });

    window.open("http://www.bca.co.uk/buy/ev-battery-grading", "_blank");
  };

  return (
    <Modal
      shouldCloseOnBlur={false}
      role="dialog"
      open={isOpen}
      placement="right"
      width={598}
    >
      <Modal.CloseBar onCloseClick={onClose}></Modal.CloseBar>
      <Modal.Header>
        <Typography variant="h5" color="fg_dark_3">
          BCA EV Battery Health Grading
        </Typography>
      </Modal.Header>
      <Modal.Body display="flex" flexDirection="column" gap="xl">
        <Typography variant="body-large" color="fg_dark_3">
          A comprehensive analysis of the functionality of the HV / traction
          battery. Various areas of the battery are tested and our health score
          is produced.
        </Typography>

        <BatteryGradesTable batteryGrade={data?.batteryCondition} />

        <Box ref={modalBody} display="flex" flexDirection="column" gap="md">
          <Typography variant="h6" color="fg_dark_3">
            Detailed report
          </Typography>
          <Typography variant="body-large" color="fg_dark_3">
            The AVILOO Flash Test generates a detailed report that provides the
            basis for our health score. This can be viewed, downloaded and
            shared with your buyers.
          </Typography>
          <Typography variant="body-large" color="fg_dark_3">
            * The grading and test results in this report are provided for
            information and are NOT eligible to be claimed on.
          </Typography>
          <Link inline={false} onClick={handleBatteryHealthGrading}>
            More on BCA EV Battery Health Grading
          </Link>
        </Box>
      </Modal.Body>
      <Modal.Actions gap="lg">
        <Button
          text="Terms and Conditions"
          variant="secondary"
          onClick={handleTermsAndConditions}
        />
        <Button
          disabled={!Boolean(data?.reportUrl)}
          text="View report"
          onClick={handleViewReport}
        />
      </Modal.Actions>
    </Modal>
  );
};

export default BatteryGradeSlideout;
